import cn from 'classnames';
import * as React from 'react';
import { useContext, useEffect, useRef } from 'react';
import MediaQuery from 'react-responsive';
import { FieldValidator } from 'final-form';
import { Field, FieldRenderProps, FormSpy } from 'react-final-form';
import { AdditionalProps } from 'react-onclickoutside';
import { useTranslation } from 'react-i18next';

import LocationAutocomplete from '../../../../../../LocationAutocomplete';
import { OnSetLocationHandler } from '../../../../../store/segments/actions';
import { LocationsConfig, LocationType, WebskyEventType } from '../../../../../../types';
import { Location } from '../../../../../../LocationAutocomplete/types';
import { emitEvent, MOBILE_MIN_WIDTH, useIsMobile, disableAutocompleteProps } from '../../../../../../utils';
import Value from './Value/Value';
import Tooltip from '../../../../../../BaseComponents/Tooltip/Tooltip';
import { ConfigContext } from '../../../../../../context';
import { useTheme } from '../../../../../../theme';
import { useRenderers } from '../../../../../../renderProps';
import { CdnIcon } from '@shared/ui';
import SyncFieldFocus from './SyncFieldFocus/SyncFieldFocus';

export interface RenderInputProps {
	inputProps: React.InputHTMLAttributes<HTMLInputElement>;
	isOpen: boolean;
	isLoading: boolean;
}

export interface LocationRenderers {
	renderInput?: (props: RenderInputProps) => React.ReactNode;
}

export interface Props extends LocationRenderers {
	type: LocationType;
	segmentId: number;
	setLocation: OnSetLocationHandler<void>;
	locations: LocationsConfig;
	recentSearches: Location[];
	className?: string;
	onSwap?: () => void;
	withMultiCity?: boolean;
}

export const IGNORE_CLICKOUTSIDE_CLASS = 'ignore_clickoutside';

let lastVisibleError = '';

export default React.memo<Props>(
	({
		type,
		className = '',
		setLocation,
		locations,
		onSwap,
		recentSearches,
		segmentId,
		withMultiCity,
		renderInput
	}) => {
		const { Segment, Location: css } = useTheme('SearchForm');
		const { t } = useTranslation('SearchForm');
		const alternative = useContext(ConfigContext).SearchForm.mobileFormType !== 'common';
		const location = locations[type];
		const { SearchFormSegmentValue: SearchFormSegmentValueOverride = Value } = useRenderers();
		const additionalProps: AdditionalProps = {
			outsideClickIgnoreClass: IGNORE_CLICKOUTSIDE_CLASS
		};
		const isMobile = useIsMobile();

		const autocompleteRef = useRef<HTMLDivElement>();
		const inputRef = useRef<HTMLInputElement>();

		useEffect(() => {
			const handleClickOutside = (e: PointerEvent) => {
				if (
					e.target instanceof Node &&
					inputRef.current &&
					autocompleteRef.current &&
					!inputRef.current.contains(e.target) &&
					!autocompleteRef.current.contains(e.target)
				) {
					inputRef.current.blur();
				}
			};

			document.addEventListener('pointerdown', handleClickOutside);

			return () => {
				document.removeEventListener('pointerdown', handleClickOutside);
			};
		}, []);

		const validate: FieldValidator<string> = (value, allValues, meta): string | void => {
			const data: LocationsConfig = meta.data;

			if (!data || !data[type]) {
				return t('Please select {{type}} airport', {
					type: t(type)
				}) as string;
			}

			if (data && data.arrival && data.departure && data.arrival.iata === data.departure.iata) {
				return t('Please select different airports') as string;
			}
		};

		const onChange = (value: Location) => {
			setLocation(value, type);
		};

		const onNearestAirportHandler = (locations: Location[]) => {
			if (type === LocationType.Departure && segmentId === 0) {
				emitEvent(autocompleteRef?.current, {
					type: WebskyEventType.NearestAirportsLoaded,
					data: locations.map(location => ({
						iata: location.iata,
						name: location.name
					}))
				});
			}
		};

		const getRenderInput = (fieldState: FieldRenderProps<string, HTMLInputElement>) => (
			isOpen: boolean,
			isLoading: boolean,
			inputProps: React.InputHTMLAttributes<HTMLInputElement>
		): React.ReactNode => {
			const { onChange, onFocus, onBlur, ...restProps } = inputProps;
			const finalProps: React.InputHTMLAttributes<HTMLInputElement> = restProps;

			finalProps.name = fieldState.input.name;

			/**
			 * WEBSKY-8579:
			 * В мобильном автокомплите blur/focus инпута не срабатывают.
			 * Синхронизируем formState.active с isOpen диалогового окна, чтобы тултипы корректно появлялись/исчезали.
			 */
			const SyncFieldFocusComponent = isMobile ? (
				<SyncFieldFocus isOpen={isOpen} onFocus={fieldState.input.onFocus} onBlur={fieldState.input.onBlur} />
			) : null;

			if (inputProps.onBlur) {
				finalProps.onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
					onBlur(event);
					fieldState.input.onBlur(event);
				};
			}

			if (inputProps.onFocus) {
				finalProps.onFocus = (event: React.FocusEvent<HTMLInputElement>) => {
					onFocus(event);
					fieldState.input.onFocus(event);
				};
			}

			if (inputProps.onChange) {
				finalProps.onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
					onChange(event);
					fieldState.input.onChange(event);
				};
			}

			if (renderInput) {
				return (
					<>
						{SyncFieldFocusComponent}
						{renderInput({ inputProps: finalProps, isOpen, isLoading })}
					</>
				);
			}

			const autocompleteFocus = (event: React.MouseEvent) => {
				if (inputRef.current) {
					inputRef.current.focus();
				}

				inputProps.onFocus(event as any);
			};

			return (
				<>
					<MediaQuery minWidth={+MOBILE_MIN_WIDTH + 1}>
						<input
							{...finalProps}
							data-test-id={
								type === LocationType.Departure
									? 'search-form-origin-field'
									: 'search-form-destination-field'
							}
							data-direction={type === LocationType.Departure ? 'departure' : 'arrival'}
							className={cn(IGNORE_CLICKOUTSIDE_CLASS, Segment.input, className, css.input, {
								[css.input_withoutPlaceholder]: !!location,
								[css.input_departure]: type === LocationType.Departure,
								[css.input_arrival]: type === LocationType.Arrival
							})}
							ref={inputRef}
							placeholder={t(type === LocationType.Departure ? 'From' : 'To')}
							{...disableAutocompleteProps}
						/>
					</MediaQuery>

					<MediaQuery maxWidth={MOBILE_MIN_WIDTH}>
						{SyncFieldFocusComponent}

						<div
							onClick={inputProps.onFocus as any}
							data-test-id={
								type === LocationType.Departure
									? 'search-form-origin-field'
									: 'search-form-destination-field'
							}
							data-direction={type === LocationType.Departure ? 'departure' : 'arrival'}
							className={cn(Segment.input, className, css.input, {
								[css.input_withoutPlaceholder]: !!location,
								[css.input_departure]: type === LocationType.Departure,
								[css.input_arrival]: type === LocationType.Arrival
							})}
						>
							{!location && (
								<div className={css.input_icon}>
									<CdnIcon icon="Info/Locations" />
								</div>
							)}
							<span className={Segment.input_text}>
								{t(type === LocationType.Departure ? 'From' : 'To')}
							</span>
						</div>
					</MediaQuery>

					{onSwap && (!isOpen || !isLoading) && !(isMobile && withMultiCity) && (
						<button
							type="button"
							data-test-id="search-form-swap"
							className={cn(css.switcher, {
								[css.switcher_highlighted]: isOpen,
								[css.switcher_alternative]: alternative
							})}
							tabIndex={-1}
							onClick={onSwap}
						>
							<MediaQuery minWidth={+MOBILE_MIN_WIDTH + 1}>
								<CdnIcon icon="Arrow/Swap" />
							</MediaQuery>
							<MediaQuery maxWidth={MOBILE_MIN_WIDTH}>
								{alternative ? <CdnIcon icon="Arrow/Swap" /> : <CdnIcon icon="Arrow/Swap Vert" />}
							</MediaQuery>
						</button>
					)}

					<SearchFormSegmentValueOverride
						className={Segment.location__value}
						type={type}
						isOpen={isOpen}
						isLoading={isLoading}
						location={location}
						inputValue={inputProps.value as string}
						withMultiCity={withMultiCity}
						segmentId={segmentId}
						onClick={autocompleteFocus}
					/>
				</>
			);
		};

		return (
			<FormSpy
				subscription={{
					submitFailed: true,
					active: true
				}}
			>
				{formState => (
					<Field
						name={`${type}_${segmentId}`}
						validate={validate}
						subscription={{
							data: true,
							error: true
						}}
					>
						{fieldState => {
							if (location !== null || typeof (fieldState.meta.data as any)[type] !== 'undefined') {
								formState.form.mutators.setFieldData(fieldState.input.name, locations);
							}

							if (fieldState.meta.error) {
								lastVisibleError = fieldState.meta.error;
							}

							return (
								<Tooltip
									placement={isMobile ? 'bottom' : 'top'}
									title={lastVisibleError}
									open={!!fieldState.meta.error && formState.submitFailed && !formState.active}
								>
									<LocationAutocomplete
										type={type}
										locations={locations}
										className={cn(css.autocomplete, {
											[css.autocomplete_alternative]: alternative
										})}
										onSelect={onChange}
										recentSearches={recentSearches}
										inputRenderer={getRenderInput(fieldState)}
										onClickOutsideProps={additionalProps}
										ref={autocompleteRef}
										onNearestAirports={onNearestAirportHandler}
									/>
								</Tooltip>
							);
						}}
					</Field>
				)}
			</FormSpy>
		);
	}
);
